import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Input } from "@shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  Pencil,
  Plus,
  Search,
  Calendar,
  User,
  Check,
  X,
  FileCog,
  ArrowRight,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { Alert, AlertDescription } from "@shared/ui/alert";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useWorkOrderTypes,
  useWorkOrderSubTypes,
  useCreateWorkOrderSubType,
  useUpdateWorkOrderSubType,
  useToggleWorkOrderSubTypeStatus,
} from "../../../../hooks";
import { WorkOrderType } from "../../../../types";

// ====================== HOOKS ======================

// Custom hook for debounced search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ====================== TYPES ======================

interface SubTypeFormData {
  name: string;
  relatedType: string;
  description?: string;
}

// ====================== COMPONENTS ======================

// Sub-type card component for better reusability
const SubTypeCard: React.FC<{
  subType: WorkOrderType;
  onEdit: (subType: WorkOrderType) => void;
  onToggleActive: (subType: WorkOrderType) => void;
  isUpdating: boolean;
  currentSubType: WorkOrderType | null;
}> = ({ subType, onEdit, onToggleActive, isUpdating, currentSubType }) => {
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md hover:border-primary/20 group">
      <CardContent className="p-0">
        <div className="p-4 border-b bg-muted/20">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-2">
              <FileCog className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <h4 className="font-medium">{subType.name}</h4>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-sm py-1 text-left text-grey">
                    Type: {subType.hierarchyNames.sopType}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {subType.description}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(subType)}
              className="opacity-0 group-hover:opacity-100 hover:bg-background/80"
              disabled={isUpdating}
            >
              {isUpdating && currentSubType?.id === subType.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Pencil className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="p-4">
          <div className="flex gap-2 items-center text-sm text-muted-foreground mb-2">
            <User className="h-3.5 w-3.5" />
            <p>{subType.createdBy}</p>
          </div>
          <div className="flex gap-2 items-center text-sm text-muted-foreground mb-4">
            <Calendar className="h-3.5 w-3.5" />
            <p>{subType.createdDate}</p>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <span className="text-sm">
              {subType.isActive ? (
                <span className="flex items-center gap-1.5 text-green-600 bg-green-50 dark:bg-green-950/30 px-2 py-1 rounded-full text-xs font-medium">
                  <Check className="h-3 w-3" /> Active
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-gray-600 bg-gray-50 dark:bg-gray-800/30 px-2 py-1 rounded-full text-xs font-medium">
                  <X className="h-3 w-3" /> Inactive
                </span>
              )}
            </span>
            <Switch
              checked={subType.isActive}
              onCheckedChange={() => onToggleActive(subType)}
              disabled={isUpdating}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Empty state component
const EmptyState: React.FC<{
  hasSearchQuery: boolean;
  onAddNew: () => void;
}> = ({ hasSearchQuery, onAddNew }) => {
  return (
    <div className="col-span-full py-12 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
      <div className="flex flex-col items-center gap-2">
        <Search className="h-10 w-10 text-muted-foreground/50" />
        <h4>No work order sub-types found</h4>
        <p className="text-sm">
          {hasSearchQuery
            ? "Try adjusting your search or add a new work order sub-type"
            : "No work order sub-types available"}
        </p>
        <Button variant="outline" className="mt-2" onClick={onAddNew}>
          <Plus className="mr-2 h-4 w-4" /> Add New Sub-Type
        </Button>
      </div>
    </div>
  );
};

// ====================== MAIN COMPONENT ======================

const SubTypesTab: React.FC = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentSubType, setCurrentSubType] = useState<WorkOrderType | null>(
    null
  );

  // Add state to track if we're in cancel mode
  const [isCanceling, setIsCanceling] = useState(false);

  // Get search query from URL
  const searchQuery = searchParams.get("search") || "";

  // Debounce the search input for better UX (but server handles the actual search)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update URL when debounced search changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (debouncedSearchInput.trim()) {
      newSearchParams.set("search", debouncedSearchInput.trim());
    } else {
      newSearchParams.delete("search");
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [debouncedSearchInput, searchParams, setSearchParams]);

  // API hooks - now using server-side search
  const { data: types = [] } = useWorkOrderTypes();
  const { data: subTypes = [] } = useWorkOrderSubTypes(searchQuery);

  const createSubTypeMutation = useCreateWorkOrderSubType();
  const updateSubTypeMutation = useUpdateWorkOrderSubType();
  const toggleStatusMutation = useToggleWorkOrderSubTypeStatus();

  // Memoized options following the same pattern as ServiceDetailForm
  const getWorkOrderTypeOptions = useMemo(() => {
    if (!types || !Array.isArray(types)) {
      return [];
    }
    return types
      .filter((type) => type.isActive === true)
      .map((type) => ({
        value: type.name,
        label: type.name,
      }));
  }, [types]);

  // Form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "relatedType",
        type: "select",
        label: "Work Order Type",
        placeholder: "Select work order type...",
        required: true,
        disabled: !!currentSubType, // Disable when editing
        options: getWorkOrderTypeOptions,
        helperText: currentSubType
          ? "Type cannot be changed when editing"
          : "Choose the parent work order type for this sub-type",
        className: "pt-2",
      },
      {
        name: "name",
        type: "text",
        label: "Sub-Type Name",
        placeholder: "Enter sub-type name...",
        required: true,
        minLength: 2,
        maxLength: 100,
        helperText: "Provide a clear, descriptive name for the sub-type",
      },
      {
        name: "description",
        type: "textarea",
        label: "Description",
        placeholder: "Enter description...",
        rows: 3,
        maxLength: 500,
        className: "pt-2",
      },
    ],
    [getWorkOrderTypeOptions, currentSubType]
  );

  // Create form instance using FormService - following established pattern
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      relatedType: "",
      description: "",
    },
  });

  // Update form when editing a sub-type - but not during cancel
  useEffect(() => {
    if (isCanceling) return; // Skip form updates during cancel

    if (currentSubType && isEditModalOpen) {
      const populateData = {
        name: currentSubType.name,
        relatedType: currentSubType.hierarchyNames.sopType,
        description: currentSubType.description,
      };
      FormService.populateForm(form, populateData, formFields);
    } else if (isAddModalOpen) {
      const defaultValues = {
        name: "",
        relatedType: "",
        description: "",
      };
      FormService.populateForm(form, defaultValues, formFields);
    }
  }, [
    currentSubType,
    isEditModalOpen,
    isAddModalOpen,
    form,
    formFields,
    isCanceling,
  ]);

  // Handle field changes - simplified like other components
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // Prevent any field changes during cancel
      if (isCanceling) return;
    },
    [isCanceling]
  );

  // Handle form submission
  const handleSubmit = async (data: SubTypeFormData) => {
    if (isCanceling) return; // Prevent submit during cancel

    try {
      if (currentSubType) {
        const updatePayload = {
          code: currentSubType.code,
          name: data.name.trim(),
          description: data.description?.trim(),
        };
        // Update existing sub-type
        await updateSubTypeMutation.mutateAsync(updatePayload);
        setIsEditModalOpen(false);
      } else {
        const selectedType = types.find(
          (type) => type.name === data.relatedType
        );

        if (!selectedType) {
          toast({
            title: "Invalid Type",
            description: "Selected work order type not found",
            variant: "destructive",
          });
          return;
        }

        const createPayload = {
          name: data.name.trim(),
          code: selectedType.code,
          description: data.description?.trim(),
        };
        // Create new sub-type
        await createSubTypeMutation.mutateAsync(createPayload);
        setIsAddModalOpen(false);
      }

      // Reset form state - following established pattern
      FormService.resetForm(form, formFields);
      setCurrentSubType(null);
    } catch (error) {
      // Handle error
    }
  };

  const handleReset = useCallback(() => {
    if (isCanceling) return; // Prevent reset during cancel
    FormService.resetForm(form, formFields);
  }, [form, formFields, isCanceling]);

  // Event handlers
  const handleEditClick = useCallback((subType: WorkOrderType) => {
    setCurrentSubType(subType);
    setIsEditModalOpen(true);
  }, []);

  const handleToggleActive = useCallback(
    async (subType: WorkOrderType) => {
      try {
        await toggleStatusMutation.mutateAsync({
          code: subType.code,
          isActive: !subType.isActive,
          name: subType.name,
        });
      } catch (error) {
        // Handle error
      }
    },
    [toggleStatusMutation]
  );

  // Handle modal close with cancel protection
  const handleAddModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsAddModalOpen(false);
    setCurrentSubType(null);

    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  const handleEditModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsEditModalOpen(false);
    setCurrentSubType(null);

    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    []
  );

  return (
    <div className="">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background pt-2 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col items-start">
          <h3 className="page-subheading">Work Order Sub-Types</h3>
          <p className="page-subtitle">
            Create and manage detailed categories under each work order type.
          </p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          disabled={createSubTypeMutation.isPending}
        >
          {createSubTypeMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Add New Sub-Type
        </Button>
      </div>

      {/* Grid */}
       <div className="relative pb-4">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search work order sub-types..."
              className="pl-8 w-full sm:w-[250px]"
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.isArray(subTypes) &&
          subTypes.map((subType) => (
            <SubTypeCard
              key={subType.id}
              subType={subType}
              onEdit={handleEditClick}
              onToggleActive={handleToggleActive}
              isUpdating={toggleStatusMutation.isPending}
              currentSubType={currentSubType}
            />
          ))}

        {Array.isArray(subTypes) && subTypes.length === 0 && (
          <EmptyState
            hasSearchQuery={!!searchQuery}
            onAddNew={() => setIsAddModalOpen(true)}
          />
        )}
      </div>

      {/* Add Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={handleAddModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Work Order Sub-Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={createSubTypeMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleAddModalClose}
                    disabled={createSubTypeMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={createSubTypeMutation.isPending}
                  >
                    {createSubTypeMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Save Sub-Type"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={handleEditModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Work Order Sub-Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={updateSubTypeMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleEditModalClose}
                    disabled={updateSubTypeMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={updateSubTypeMutation.isPending}
                  >
                    {updateSubTypeMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Sub-Type"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubTypesTab;
